import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Flame, HeartHandshake } from "lucide-react";
import { api } from "@/lib/api";
import { PartnerNudge } from "@/components/PartnerNudge";

const pretty = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

export default function PartnerView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/share/public/${token}`).then(({ data }) => setData(data)).catch(() => setError("This link isn't active any more."));
  }, [token]);

  return (
    <div className="min-h-screen px-5 sm:px-8 py-10 sm:py-16">
      <div className="max-w-3xl mx-auto rise">
        <Link to="/" className="flex items-center gap-3" data-testid="partner-brand">
          <span className="leaf-mark" />
          <span className="font-serif-display text-2xl text-[#2C221E]">Meridia</span>
        </Link>

        {error && (
          <div className="mt-12 card-paper p-8" data-testid="partner-inactive">
            <h1 className="font-serif-display text-3xl text-[#2C221E]">{error}</h1>
            <p className="mt-3 text-[#52443E]">She may have turned sharing off. That's hers to decide.</p>
          </div>
        )}

        {data && <Summary data={data} />}
        {data && <PartnerNudge token={token} options={data.nudge_options || []} initial={data.nudge} />}
        {data && (
          <p className="mt-10 text-xs text-[#8C7A70] max-w-xl">
            This page shows only what she chose to share. Her notes and conversations stay private. Meridia offers companionship and tracking, not medical advice.
          </p>
        )}
      </div>
    </div>
  );
}

function Summary({ data }) {
  const s = data.summary;
  const chart = (s.days || []).map((d) => ({ date: d.date.slice(5), sleep: d.sleep, energy: d.energy }));
  return (
    <>
      <p className="mt-12 text-xs uppercase tracking-[0.22em] text-[#8C7A70] font-semibold" data-testid="partner-range">
        {pretty(data.week_start)} – {pretty(data.week_end)} · shared with you
      </p>
      <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#2C221E] leading-tight" data-testid="partner-title">
        {data.first_name}'s week, at a glance
      </h1>
      <p className="mt-3 text-[#52443E] max-w-xl">
        Not the whole story — just enough that she doesn't have to explain it out loud.
      </p>

      <div className="mt-10 card-paper p-6 sm:p-8 border-l-4 border-l-[#C26D53]" data-testid="partner-help-line">
        <div className="flex items-center gap-2 text-[#C26D53]">
          <HeartHandshake size={18} />
          <p className="text-xs uppercase tracking-[0.18em] font-semibold">How to help this week</p>
        </div>
        <p className="mt-3 font-serif-display text-xl sm:text-2xl text-[#2C221E] leading-snug">{data.help_line}</p>
      </div>

      {s.entries === 0 ? (
        <div className="mt-6 card-paper p-6 sm:p-8 text-[#52443E]" data-testid="partner-empty">No check-ins this week.</div>
      ) : (
        <>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <Stat label="Sleep" value={`${s.avg_sleep}/5`} sub="average quality" testid="partner-avg-sleep" />
            <Stat label="Energy" value={`${s.avg_energy}/5`} sub="average" testid="partner-avg-energy" />
            <Stat label="Hot flashes" value={<span className="inline-flex items-center gap-2"><Flame size={18} className="text-[#C26D53]" />{s.hot_flashes}</span>} sub="across the week" testid="partner-flashes" />
          </div>

          <div className="mt-6 card-paper p-6 sm:p-8" data-testid="partner-chart">
            <h2 className="font-serif-display text-xl text-[#2C221E]">Sleep & energy</h2>
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart}>
                  <defs>
                    <linearGradient id="ps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5F7463" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#5F7463" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="pe" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4A359" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#D4A359" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" stroke="#8C7A70" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 5]} stroke="#8C7A70" tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: "#FFFDF9", border: "1px solid #E5D9C8", borderRadius: 12 }} />
                  <Area type="monotone" dataKey="sleep" name="Sleep" stroke="#5F7463" fill="url(#ps)" strokeWidth={2} />
                  <Area type="monotone" dataKey="energy" name="Energy" stroke="#D4A359" fill="url(#pe)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-6 card-paper p-6 sm:p-8" data-testid="partner-moods">
            <h2 className="font-serif-display text-xl text-[#2C221E]">Words she reached for</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {s.moods.map((m) => (
                <span key={m} className="chip" data-active="true">{m}</span>
              ))}
            </div>
            <p className="mt-4 text-sm text-[#73635B]">
              Heavy brain fog on {s.heavy_fog_days} {s.heavy_fog_days === 1 ? "day" : "days"}. {s.entries} of 7 days checked in.
            </p>
          </div>
        </>
      )}
    </>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="card-paper p-6" data-testid={testid}>
      <p className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">{label}</p>
      <p className="mt-2 font-serif-display text-4xl text-[#2C221E]">{value}</p>
      <p className="text-sm text-[#73635B]">{sub}</p>
    </div>
  );
}
